/**
 * Leads list — `leads.html` ported, against the live §4 API.
 *
 * The prototype is the design reference and this follows it closely: the same
 * list-view picker, the same toolbar, the same twelve columns, the same three
 * views behind the same segmented switch, the same filter rail, the same
 * inline-edit save bar, the same footer.
 *
 * What differs, and why — because a port that quietly drops the parts that do
 * not work is indistinguishable from one that forgot them:
 *
 * - **Saved views are query parameters, not saved views.** `?view=` answers 501
 *   (§10, `cfg.saved_view`), so each entry in the picker is a set of explicit
 *   filters the list endpoint does support. They behave identically; they are
 *   just not stored anywhere, so nobody can create one. "New list view" and
 *   "List view settings" are therefore not in the menu.
 * - **The counts beside each view are gone.** The prototype hardcodes them.
 *   Real ones would be one request per view, seven round trips at ~1.3s each,
 *   to fill in a dropdown most people open rarely. The active view shows its
 *   real total in the subline instead.
 * - **"Recently Viewed" is gone.** Nothing records what this user looked at.
 * - **Change Owner is real**, in the bulk bar, the `⋯` menu, the row menu and
 *   the detail page — `POST /leads/{id}/assign` / `bulk-assign` are live,
 *   gated `leads:assign`, held only by admin and manager in the seeded roles.
 *   `PATCH` still omits `owner_id` on purpose (§4: "Assignment is its own
 *   permission, its own audit action and usually notifies someone") — this is
 *   the one door to it. **Transfer to queue** (`owner_id: null`, in the `⋯`
 *   menu) is real too, but only for a lead still in **New** status — anything
 *   worked has activity investment a silent return to the queue would orphan;
 *   the server enforces this and a mixed selection reports which ones refused.
 * - **Import** is real (`LeadImportWizard.jsx` — docs/modules/leadImport.model.md),
 *   gated on `leads:import` (manager and superadmin only — reps are denied it,
 *   seed 02). **Send Email** is real too. **Convert, Clone** are 501 or §14
 *   and say so.
 * - **Pagination is cursor-based**, so the footer has Previous/Next rather than
 *   the prototype's numbered 1 · 2 · 3 … 107. §1 forbids offsets, and a page
 *   number is an offset wearing a hat.
 *
 * Bootstrap's JavaScript is not loaded — only its CSS — so the two dropdowns
 * here toggle `.show` themselves.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useLeads, useAllLeads, useCreateLead, useUpdateLead, useDeleteLead, useBulkLeads, useAssignLeads, useLeadOptions,
} from './useLeads.js';
import { useCursorPages } from '../../hooks/useCursorPages.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { useAuth } from '../../hooks/useAuth.js';
import { isMock } from '../../api/mockFallback.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';
import { ErrorState, MockBadge, ChipSelect } from '../../components/common/index.jsx';
import { usePartners } from '../partners/usePartners.js';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { toRow, mutationRef } from './leadShape.js';
import { LeadTable, SplitView, KanbanView, TABLE_COLUMNS } from './LeadViews.jsx';
import {
  LeadFormModal, DeleteLeadModal, BulkPatchModal, ChangeOwnerModal, TransferToQueueModal, NotBuiltModal, ColumnsModal,
} from './LeadModals.jsx';
import { AddToCampaignModal } from '../campaigns/CampaignModals.jsx';
import { EmailComposerModal } from '../emailTemplates/EmailComposerModal.jsx';
import { LeadImportWizard } from './LeadImportWizard.jsx';
import { relativeTime } from '../../utils/formatters.js';

/** Rows per page. The footer's "Showing N of M" reads `rows.length`, not this
 *  constant, so it stays correct on its own whenever this changes. */
const PAGE_LIMIT = 20;

/**
 * Which columns the reader has hidden, remembered per browser.
 *
 * `localStorage`, not the server: `iam.app_user.ui_prefs` is the right home for
 * this and there is no endpoint that writes it, and a hidden column is exactly
 * the kind of per-viewer convenience localStorage is for — it does not need to
 * follow anyone to another machine, and losing it costs one click. Wrapped in
 * try/catch because the accessor itself throws in a browser set to block site
 * data, which would otherwise take the whole screen down.
 */
const HIDDEN_KEY = 'entiovi.leads.hiddenColumns';

/** Columns that cannot be hidden: identity, selection and the row menu. */
const FIXED_COLUMNS = new Set(['__sel', 'name', '__menu']);

function readHiddenColumns() {
  try {
    const raw = localStorage.getItem(HIDDEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((k) => !FIXED_COLUMNS.has(k)) : [];
  } catch {
    return [];
  }
}

function writeHiddenColumns(keys) {
  try {
    localStorage.setItem(HIDDEN_KEY, JSON.stringify(keys));
  } catch { /* Private window, or site data blocked. The session still works. */ }
}

/**
 * The prototype's list views, as filters `GET /leads` understands.
 *
 * `open` is spelled out as status codes rather than "not Converted, Junk"
 * because the endpoint takes an inclusion list, not an exclusion — and spelling
 * it out means a new status added in master data does not silently join the
 * open set without anyone deciding it should.
 */
const OPEN_STATUSES = 'NEW,WRK,NUR,QLF,UNQ';

/** The seven statuses `md.lead_status` seeds, for the rail's Lead Status filter. */
const STATUS_OPTIONS = [
  ['NEW', 'New'], ['WRK', 'Working'], ['NUR', 'Nurturing'], ['QLF', 'Qualified'],
  ['UNQ', 'Unqualified'], ['CNV', 'Converted'], ['JUNK', 'Junk'],
];

const VIEWS = [
  { name: 'All Open Leads', icon: 'bi-pin-angle-fill text-warning', params: { status: OPEN_STATUSES }, pinned: true },
  { name: 'My Leads', icon: 'bi-person', params: { owner_id: 'me' } },
  { name: 'My Unread Leads', icon: 'bi-envelope', params: { owner_id: 'me', status: 'NEW' } },
  { name: "Today's Leads", icon: 'bi-calendar-day', params: { followup: 'today' } },
  { name: 'Unassigned Queue', icon: 'bi-inbox', params: { owner_id: 'none' } },
  {
    name: 'SLA at Risk',
    icon: 'bi-stopwatch',
    params: { sla: 'at_risk' },
    /* The prototype means "overdue or unscheduled"; the API means "due within
       two hours and not yet responded to" (§5.5). Different definitions, so the
       menu says which one this is rather than quietly redefining the view. */
    note: 'First response due within 2 hours',
  },
];

/** Endpoints the prototype offers that the API does not, with the reason.
 *  Change Owner is not in here — see `canAssign`/`ChangeOwnerModal`; it calls
 *  the live `POST /leads/{id}/assign` / `bulk-assign`, gated `leads:assign`.
 *  `email` used to live here too — §8 is real now, so Send Email opens
 *  `EmailComposerModal` in bulk mode, one send per selected lead. */
const NOT_BUILT = {
  export: {
    title: 'Export is not available',
    why: 'Export is a job, not a download: the API issues a presigned URL and the bytes never pass through it (§1). Neither the job runner nor the object store is wired up. The Master Data screen exports client-side because it holds the whole table in memory; a 50-thousand-row lead list cannot.',
    endpoint: 'POST /export/jobs',
    section: 'docs/API.md §14, not implemented',
  },
};

/* -------------------------------------------------------------------------- */

export default function LeadsListPage() {
  const { can } = useAuth();
  const dispatch = useDispatch();

  const [viewName, setViewName] = useState(VIEWS[0].name);
  const view = VIEWS.find((v) => v.name === viewName) ?? VIEWS[0];

  const [mode, setMode] = useState('table');          // table | split | kanban
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('-score');
  const [railOpen, setRailOpen] = useState(false);
  const [scoreGte, setScoreGte] = useState('');
  const [quick, setQuick] = useState({});             // hot / unassigned / nofollowup / overdue
  const [statusFilter, setStatusFilter] = useState([]); // status codes, empty = the view's own default
  const [partnerId, setPartnerId] = useState(''); // docs/modules/partnerLeads.model.md
  const [selected, setSelected] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [emailLeads, setEmailLeads] = useState([]);

  /** Held inline edits, `"<leadId>.<field>" → value`. Applied by the save bar. */
  const [edits, setEdits] = useState(() => new Map());

  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState([]);       // rows awaiting a reason
  const [bulkField, setBulkField] = useState(null);   // 'status_id' | 'campaign_id'
  const [notBuilt, setNotBuilt] = useState(null);
  const [campaignAddIds, setCampaignAddIds] = useState(null); // lead ids, or null when the modal is closed
  const [assigningRows, setAssigningRows] = useState([]);     // rows awaiting a new owner
  const [transferringRows, setTransferringRows] = useState([]); // rows awaiting queue-transfer confirmation
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(readHiddenColumns);

  const setHidden = (keys) => { setHiddenColumns(keys); writeHiddenColumns(keys); };
  const columns = useMemo(
    () => TABLE_COLUMNS.filter((c) => !hiddenColumns.includes(c.key)),
    [hiddenColumns],
  );

  const debouncedSearch = useDebounce(search, 250);
  const pages = useCursorPages(PAGE_LIMIT);

  const options = useLeadOptions();
  const create = useCreateLead();
  const update = useUpdateLead();
  const remove = useDeleteLead();
  const bulk = useBulkLeads();
  const assign = useAssignLeads();

  /**
   * The query the list runs.
   *
   * Quick filters overlay the view's own parameters, and the order matters:
   * "Unassigned" inside "My Leads" has to mean the intersection is empty, not
   * "ignore the view". So the quick filter wins on the key it sets.
   */
  const filters = useMemo(() => ({
    ...view.params,
    ...(statusFilter.length ? { status: statusFilter.join(',') } : {}),
    ...(quick.hot ? { rating: 'Hot' } : {}),
    ...(quick.unassigned ? { owner_id: 'none' } : {}),
    ...(quick.nofollowup ? { followup: 'none' } : {}),
    ...(quick.overdue ? { followup: 'overdue' } : {}),
    ...(scoreGte ? { score_gte: Number(scoreGte) } : {}),
    ...(partnerId ? { referred_by_partner_id: partnerId } : {}),
    ...(debouncedSearch ? { q: debouncedSearch } : {}),
    sort,
  }), [view.params, statusFilter, quick, scoreGte, partnerId, debouncedSearch, sort]);

  const listParams = useMemo(() => ({ ...pages.params, ...filters }), [pages.params, filters]);

  const list = useLeads(listParams);
  /* The board needs every row in the filter, not this page's — see useAllLeads. */
  const board = useAllLeads(filters, mode === 'kanban');

  const source = mode === 'kanban' ? board : list;
  const rows = useMemo(() => (source.data?.data ?? []).map(toRow), [source.data]);

  /* Anything that changes the result set invalidates the cursor and the
     selection: a cursor from the old set means nothing, and a selection of ids
     that are no longer on screen is a bulk action nobody can review. */
  const reset = () => {
    pages.reset();
    setSelected([]);
    setEdits(new Map());
  };

  useEffect(() => { setActiveId(null); }, [viewName]);

  const filterCount =
    (statusFilter.length ? 1 : 0) +
    Object.values(quick).filter(Boolean).length +
    (scoreGte ? 1 : 0) +
    (partnerId ? 1 : 0) +
    (debouncedSearch ? 1 : 0);

  /* The prototype's subline says "filtered by Status, Owner". Ours names what
     is genuinely applied — the view's own parameters plus anything set in the
     rail — so it stays true when the reader changes something. */
  const filterSummary = useMemo(() => {
    const names = Object.keys(view.params).map((k) => PARAM_LABEL[k] ?? k);
    if (statusFilter.length) names.push('Lead Status');
    if (quick.hot) names.push('Rating');
    if (quick.unassigned) names.push('Owner');
    if (quick.nofollowup || quick.overdue) names.push('Next Follow-up');
    if (scoreGte) names.push('Lead Score');
    if (partnerId) names.push('Partner');
    if (debouncedSearch) names.push('Search');
    return [...new Set(names)].join(', ');
  }, [view.params, statusFilter, quick, scoreGte, partnerId, debouncedSearch]);

  const selectedRows = rows.filter((r) => selected.includes(r.id));
  const canEdit = can('leads', 'edit');
  const canDelete = can('leads', 'delete');
  const canAssign = can('leads', 'assign');
  const canImport = can('leads', 'import');
  /* Filtering leads by referring partner needs the partner *list* to build
     the dropdown, which is `admin.partners:view` — a different permission
     from `leads:view` — so the filter block itself only renders for whoever
     already holds it (manager/superadmin), rather than 403ing for a rep. */
  const canViewPartners = can('admin.partners', 'view');
  const partnersQuery = usePartners({ is_active: true, limit: 200 }, canViewPartners);
  const partnerOptions = canViewPartners ? (partnersQuery.data?.data ?? []).map((p) => ({ value: p.id, label: p.name })) : [];

  /* The header/`⋯`-menu Change Owner isn't tied to a row the way the bulk bar
     (only rendered once something is selected) or the row menu (always one
     specific row) are — clicking it with nothing checked would otherwise open
     a dialog offering to reassign zero leads. */
  const openChangeOwner = () => {
    if (!selectedRows.length) {
      dispatch(pushToast({ tone: 'warning', message: 'Select at least one lead first, then Change Owner.' }));
      return;
    }
    setAssigningRows(selectedRows);
  };

  /* Same nothing-selected guard as Change Owner, then a confirm dialog before
     the real write — clearing owner_id is not something a stray click on the
     `⋯` menu should trigger unprompted. The New-only rule itself is enforced
     server-side (§4 — a lead with activity investment shouldn't silently
     orphan), so a mixed selection still reports back which ones refused
     rather than the dialog needing a client-side pre-check that could drift
     from it. */
  const openTransferToQueue = () => {
    if (!selectedRows.length) {
      dispatch(pushToast({ tone: 'warning', message: 'Select at least one lead first, then Transfer to Queue.' }));
      return;
    }
    setTransferringRows(selectedRows);
  };

  const onEdit = (key, value) => {
    setEdits((prev) => {
      const next = new Map(prev);
      if (value === undefined) next.delete(key);
      else next.set(key, value);
      return next;
    });
  };

  /**
   * Apply the held inline edits.
   *
   * Grouped by lead, so three cells edited on one row are one PATCH carrying
   * three fields — not three PATCHes, which would take three round trips and
   * make the second and third fail on a stale `row_version` from the first.
   */
  const [savingEdits, setSavingEdits] = useState(false);
  const applyEdits = async () => {
    const byLead = new Map();
    for (const [key, value] of edits) {
      const [id, field] = [key.slice(0, key.lastIndexOf('.')), key.slice(key.lastIndexOf('.') + 1)];
      const patch = byLead.get(id) ?? {};
      patch[field] = field === 'estimated_value' ? Number(value) : value;
      byLead.set(id, patch);
    }

    setSavingEdits(true);
    let ok = 0;
    const failed = [];
    for (const [id, patch] of byLead) {
      const row = rows.find((r) => r.id === id);
      if (!row) continue;
      try {
        await update.mutateAsync({ id, patch, rowVersion: row.raw.row_version });
        ok += 1;
      } catch {
        /* `useUpdateLead` has already raised its own toast naming the row's
           problem, so this only needs the count for the summary. */
        failed.push(id);
      }
    }
    setSavingEdits(false);
    setEdits(new Map());

    if (ok && !failed.length) {
      dispatch(pushToast({ tone: 'success', message: `Saved ${ok} lead${ok === 1 ? '' : 's'}` }));
    }
  };

  /* ---- kanban drag ------------------------------------------------------- */
  const [moving, setMoving] = useState(null);
  const onMove = async (row, statusId, statusName) => {
    setMoving(row.id);
    try {
      await update.mutateAsync({ id: row.id, patch: { status_id: statusId }, rowVersion: row.raw.row_version });
      dispatch(pushToast({ tone: 'success', message: `${row.name} → ${statusName}` }));
    } catch { /* useUpdateLead toasts the reason. */ }
    setMoving(null);
  };

  if (source.error) {
    return (
      <>
        <LeadsHeader
          view={view} onView={(n) => { setViewName(n); reset(); }}
          total={null} sort={sort} mock={false} updatedAt={null} filterSummary=""
          canCreate={can('leads', 'create')} canAssign={canAssign} canImport={canImport} onNew={() => setFormOpen(true)}
          onNotBuilt={setNotBuilt} onImport={() => setImportOpen(true)} onCampaign={() => setCampaignAddIds(selected)}
          onChangeOwner={openChangeOwner} onTransferToQueue={openTransferToQueue}
        />
        <ErrorState error={source.error} onRetry={source.refetch} />
      </>
    );
  }

  const total = source.data?.page?.total ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT));

  return (
    <>
      <LeadsHeader
        view={view}
        onView={(n) => { setViewName(n); reset(); }}
        total={total}
        sort={sort}
        mock={isMock(source.data)}
        updatedAt={source.dataUpdatedAt}
        filterSummary={filterSummary}
        canCreate={can('leads', 'create')}
        canAssign={canAssign}
        canImport={canImport}
        onNew={() => setFormOpen(true)}
        onNotBuilt={setNotBuilt}
        onImport={() => setImportOpen(true)}
        onCampaign={() => setCampaignAddIds(selected)}
        onChangeOwner={openChangeOwner}
        onTransferToQueue={openTransferToQueue}
      />

      <div className="card-x fade-up">
        <Toolbar
          search={search}
          onSearch={(v) => { setSearch(v); reset(); }}
          pinned={view.pinned}
          mode={mode}
          onMode={setMode}
          onRefresh={source.refetch}
          refreshing={source.isFetching}
          railOpen={railOpen}
          onRail={() => setRailOpen((v) => !v)}
          filterCount={filterCount}
          hiddenCount={hiddenColumns.length}
          onColumns={() => setColumnsOpen(true)}
        />

        {selected.length > 0 && (
          <BulkBar
            count={selected.length}
            canEdit={canEdit}
            canDelete={canDelete}
            canAssign={canAssign}
            busy={bulk.isPending}
            onStatus={() => setBulkField('status_id')}
            onCampaign={() => setCampaignAddIds(selectedRows.map((r) => r.id))}
            onDelete={() => setDeleting(selectedRows)}
            onChangeOwner={openChangeOwner}
            onEmail={() => setEmailLeads(selectedRows)}
            onClear={() => setSelected([])}
          />
        )}

        <div className="d-flex align-items-stretch list-body position-relative">
          <div className={`flex-grow-1 min-w-0 ${source.isPlaceholderData ? 'tbl-busy' : ''}`}>
            {mode === 'table' && (
              <LeadTable
                rows={rows}
                isLoading={source.isLoading}
                selected={selected}
                onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
                onToggleAll={(on) => setSelected(on ? rows.map((r) => r.id) : [])}
                edits={edits}
                onEdit={onEdit}
                columns={columns}
                statusOptions={options.statuses}
                canEdit={canEdit}
                canDelete={canDelete}
                canAssign={canAssign}
                onDelete={(row) => setDeleting([row])}
                onChangeOwner={(row) => setAssigningRows([row])}
                onEmail={(row) => setEmailLeads([row])}
                onNew={can('leads', 'create') ? () => setFormOpen(true) : null}
                filtered={filterCount > 0 || viewName !== VIEWS[0].name}
                sort={sort}
                onSort={(s) => { setSort(s); reset(); }}
              />
            )}

            {mode === 'split' && (
              <SplitView
                rows={rows}
                isLoading={source.isLoading}
                activeId={activeId}
                onSelect={setActiveId}
                onDelete={(row) => setDeleting([row])}
                canDelete={canDelete}
                onEmail={(row) => setEmailLeads([row])}
              />
            )}

            {mode === 'kanban' && (
              <KanbanView
                rows={rows}
                isLoading={source.isLoading}
                statusOptions={options.statuses}
                onMove={onMove}
                moving={moving}
                canEdit={canEdit}
                truncated={Boolean(source.data?.page?.has_more)}
                total={total}
              />
            )}
          </div>

          <FilterRail
            open={railOpen}
            view={view}
            statusFilter={statusFilter}
            onStatusToggle={(code) => {
              setStatusFilter((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
              reset();
            }}
            scoreGte={scoreGte}
            onScoreGte={(v) => { setScoreGte(v); reset(); }}
            quick={quick}
            onQuick={(k) => { setQuick((q) => ({ ...q, [k]: !q[k] })); reset(); }}
            partnerId={partnerId}
            onPartnerId={(v) => { setPartnerId(v); reset(); }}
            partnerOptions={canViewPartners ? partnerOptions : null}
            onClear={() => { setStatusFilter([]); setQuick({}); setScoreGte(''); setPartnerId(''); setSearch(''); reset(); }}
            onClose={() => setRailOpen(false)}
            filterCount={filterCount}
          />
        </div>

        {edits.size > 0 && (
          <div className="save-bar">
            <i className="bi bi-info-circle-fill text-primary" aria-hidden="true" />
            <span className="t-sm">
              <strong>{edits.size}</strong> field{edits.size === 1 ? '' : 's'} edited across{' '}
              {new Set([...edits.keys()].map((k) => k.slice(0, k.lastIndexOf('.')))).size} lead
              {new Set([...edits.keys()].map((k) => k.slice(0, k.lastIndexOf('.')))).size === 1 ? '' : 's'}.
              {' '}Save or discard.
            </span>
            <div className="ms-auto d-flex gap-2">
              <button type="button" className="btn btn-cancel btn-sm-x"
                      disabled={savingEdits} onClick={() => setEdits(new Map())}>
                Discard
              </button>
              <button type="button" className="btn btn-save btn-sm-x" disabled={savingEdits} onClick={applyEdits}>
                {savingEdits ? <><ButtonSpinner />Saving…</> : 'Save'}
              </button>
            </div>
          </div>
        )}

        <div className="card-foot d-flex flex-wrap align-items-center gap-2">
          <span className="t-xs ink-3">
            {mode === 'kanban'
              ? <>{total.toLocaleString()} record{total === 1 ? '' : 's'}</>
              : <>Page {pages.pageNumber} of {totalPages} · {total.toLocaleString()} record{total === 1 ? '' : 's'}</>}
          </span>
          {mode !== 'kanban' && (
            <nav className="ms-auto d-flex align-items-center gap-2" aria-label="Pagination">
              {/* Previous/Next, not 1 · 2 · 3 … 107. §1 specifies cursor
                  pagination and forbids offsets, and you cannot address page 42
                  of a cursor-paged list without one. */}
              <button
                type="button"
                className="btn btn-soft btn-sm-x"
                disabled={!pages.canGoPrevious}
                onClick={() => { pages.goPrevious(); setSelected([]); }}
              >
                <i className="bi bi-chevron-left me-1" aria-hidden="true" />Previous
              </button>
              <button
                type="button"
                className="btn btn-soft btn-sm-x"
                disabled={!source.data?.page?.has_more}
                onClick={() => { pages.goNext(source.data?.page?.next_cursor); setSelected([]); }}
              >
                Next<i className="bi bi-chevron-right ms-1" aria-hidden="true" />
              </button>
            </nav>
          )}
        </div>
      </div>

      <LeadFormModal
        open={formOpen}
        busy={create.isPending}
        options={options}
        onClose={() => setFormOpen(false)}
        onSubmit={async (payload) => {
          try {
            await create.mutateAsync(payload);
            setFormOpen(false);
          } catch { /* useCreateLead toasts the 422 verbatim. */ }
        }}
      />

      <DeleteLeadModal
        open={deleting.length > 0}
        rows={deleting}
        busy={remove.isPending || bulk.isPending}
        onClose={() => setDeleting([])}
        onConfirm={async (reason) => {
          if (deleting.length === 1) {
            await remove.mutateAsync({
              id: deleting[0].id, reason,
              rowVersion: deleting[0].raw.row_version, label: deleting[0].name,
            }).catch(() => {});
          } else {
            await bulk.mutateAsync({ rows: deleting.map(mutationRef), reason, verb: 'deleted' }).catch(() => {});
          }
          setDeleting([]);
          setSelected([]);
        }}
      />

      <BulkPatchModal
        open={Boolean(bulkField)}
        field={bulkField}
        rows={selectedRows}
        options={options.statuses}
        busy={bulk.isPending}
        onClose={() => setBulkField(null)}
        onConfirm={async (patch) => {
          await bulk.mutateAsync({ rows: selectedRows.map(mutationRef), patch, verb: 'updated' }).catch(() => {});
          setBulkField(null);
          setSelected([]);
        }}
      />

      <ColumnsModal
        open={columnsOpen}
        all={TABLE_COLUMNS}
        fixed={FIXED_COLUMNS}
        hidden={hiddenColumns}
        onChange={setHidden}
        onClose={() => setColumnsOpen(false)}
      />

      <NotBuiltModal open={Boolean(notBuilt)} action={notBuilt} onClose={() => setNotBuilt(null)} />

      <LeadImportWizard open={importOpen} onClose={() => setImportOpen(false)} />

      <EmailComposerModal
        open={emailLeads.length > 0}
        onClose={() => setEmailLeads([])}
        bulkRecipients={emailLeads.map((r) => ({ id: r.id, label: r.name, email: r.email }))}
        bulkContextKey="lead_id"
      />

      <AddToCampaignModal
        open={campaignAddIds !== null}
        leadIds={campaignAddIds ?? []}
        onClose={() => setCampaignAddIds(null)}
      />

      <ChangeOwnerModal
        open={assigningRows.length > 0}
        rows={assigningRows}
        options={options.owners}
        busy={assign.isPending}
        onClose={() => setAssigningRows([])}
        onConfirm={async ({ ownerId, reason }) => {
          await assign.mutateAsync({ ids: assigningRows.map((r) => r.id), ownerId, reason }).catch(() => {});
          setAssigningRows([]);
          setSelected([]);
        }}
      />

      <TransferToQueueModal
        open={transferringRows.length > 0}
        rows={transferringRows}
        busy={assign.isPending}
        onClose={() => setTransferringRows([])}
        onConfirm={async () => {
          await assign.mutateAsync({ ids: transferringRows.map((r) => r.id), ownerId: null }).catch(() => {});
          setTransferringRows([]);
          setSelected([]);
        }}
      />
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Header                                                                     */
/* -------------------------------------------------------------------------- */

function LeadsHeader({
  view, onView, total, sort, mock, updatedAt, filterSummary, canCreate, canAssign, canImport, onNew, onNotBuilt,
  onImport, onCampaign, onChangeOwner, onTransferToQueue,
}) {
  return (
    <div className="lv-head mb-3">
      <div className="hl-icon" style={{ background: 'var(--grad-warm)' }}>
        <i className="bi bi-magnet-fill" aria-hidden="true" />
      </div>
      <div className="min-w-0">
        <div className="eyebrow">Leads</div>
        <ListViewPicker current={view} onSelect={onView} />
        <div className="t-xs ink-3 mt-1">
          {total === null ? '—' : total.toLocaleString()} items · sorted by {SORT_LABEL[sort] ?? sort}
          {filterSummary ? ` · filtered by ${filterSummary}` : ''}
          {/* Real, from the query's own `dataUpdatedAt`. The prototype's
              "updated a few seconds ago" is a fixed string that is wrong the
              moment you leave the tab open. */}
          {updatedAt ? <> · updated <span className="ink-2 fw-600">{relativeTime(updatedAt)}</span></> : null}
          {view.note ? ` · ${view.note}` : ''}
        </div>
      </div>

      <div className="ms-auto d-flex flex-wrap align-items-center gap-2">
        {mock && <MockBadge section="leads" />}
        {canImport && (
          <button type="button" className="btn btn-soft btn-sm-x" onClick={onImport}>
            <i className="bi bi-upload me-1" aria-hidden="true" />Import
          </button>
        )}
        <button type="button" className="btn btn-soft btn-sm-x" onClick={onCampaign}>
          <i className="bi bi-megaphone me-1" aria-hidden="true" />Add to Campaign
        </button>
        <OverflowMenu
          onNotBuilt={onNotBuilt}
          canAssign={canAssign}
          onChangeOwner={onChangeOwner}
          onTransferToQueue={onTransferToQueue}
        />
        {canCreate && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={onNew}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The prototype's `⋯` menu.
 *
 * Export is the one entry still unbuilt — kept because the menu is part of
 * the screen's shape, and naming its endpoint and section is a more useful
 * answer to "where is Export?" than an absent button. Run/Lead assignment
 * rules and Web-to-Lead setup used to sit here too; removed rather than left
 * as permanent "not available" placeholders, since none of §14's automation
 * or public-ingest module is built and none is imminent.
 * "Change owner" and "Transfer to queue" are
 * the two real entries: each opens its own confirm dialog (`ChangeOwnerModal`
 * picks a new owner; `TransferToQueueModal` just confirms, since there is
 * nothing to pick), then calls the live `POST /leads/{id}/assign` /
 * `bulk-assign` (the latter with `owner_id: null`).
 *
 * Both go through the same locked door (`leads:assign`), so both are gated on
 * `canAssign` — someone who could never perform the assignment shouldn't see
 * a menu entry for it. "Transfer to queue" carries a further server-side rule
 * `LeadsListPage.jsx` doesn't duplicate: only a lead still in New status can
 * go back to the queue — `openTransferToQueue` reports per-lead refusals
 * rather than pre-filtering the selection.
 */
function OverflowMenu({ onNotBuilt, canAssign, onChangeOwner, onTransferToQueue }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  /* A 4th slot is a real handler, replacing the "not built" explanation for
     that one entry. Every other item still just names its endpoint. */
  const ITEMS = [
    ...(canAssign ? [['bi-person-check', 'Change owner', null, onChangeOwner]] : []),
    ...(canAssign ? [['bi-people', 'Transfer to queue', null, onTransferToQueue]] : []),
    ['bi-download', 'Export', NOT_BUILT.export],
  ];

  return (
    <div className="dropdown" ref={box}>
      <button type="button" className="btn btn-soft btn-sm-x" onClick={() => setOpen((v) => !v)}
              aria-expanded={open} aria-label="More lead actions">
        <i className="bi bi-three-dots" aria-hidden="true" />
      </button>
      <ul className={`dropdown-menu dropdown-menu-end ${open ? 'show' : ''}`}>
        {ITEMS.map((item, i) => (item === null
          ? <li key="sep"><hr className="dropdown-divider" /></li>
          : (
            <li key={item[1]}>
              <button type="button" className="dropdown-item"
                      onClick={() => { setOpen(false); (item[3] ? item[3]() : onNotBuilt(item[2])); }}>
                <i className={`bi ${item[0]} me-2`} aria-hidden="true" />{item[1]}
              </button>
            </li>
          )))}
      </ul>
    </div>
  );
}

const SORT_LABEL = {
  '-score': 'Lead Score', score: 'Lead Score (low first)',
  full_name: 'Name', '-full_name': 'Name (Z–A)',
  company_name: 'Company', '-company_name': 'Company (Z–A)',
  '-estimated_value': 'Est. Value', estimated_value: 'Est. Value (low first)',
  next_followup_at: 'Next Follow-up', '-next_followup_at': 'Next Follow-up (latest first)',
};

/** The `.lv-picker` dropdown. Toggles `.show` itself — no Bootstrap JS here. */
function ListViewPicker({ current, onSelect }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  return (
    <div className="dropdown" ref={box}>
      <button type="button" className="lv-picker" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{current.name}</span><i className="bi bi-caret-down-fill" aria-hidden="true" />
      </button>
      <ul className={`dropdown-menu ${open ? 'show' : ''}`} style={{ minWidth: 262 }}>
        <li className="dropdown-header">List views</li>
        {VIEWS.map((v) => (
          <li key={v.name}>
            <button
              type="button"
              className={`dropdown-item ${v.name === current.name ? 'active' : ''}`}
              onClick={() => { onSelect(v.name); setOpen(false); }}
            >
              <i className={`bi ${v.icon} me-2`} aria-hidden="true" />{v.name}
            </button>
          </li>
        ))}
        <li><hr className="dropdown-divider" /></li>
        {/* Not "New list view" and "List view settings": both need
            `cfg.saved_view`, which is §10 and unbuilt. A menu entry that opens
            nothing is worse than a line saying why it is not there. */}
        <li>
          <span className="dropdown-item disabled t-2xs" style={{ whiteSpace: 'normal' }}>
            These are filter presets, not saved views — §10 saved views are not
            implemented, so they cannot be edited or added to.
          </span>
        </li>
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Toolbar, bulk bar, filter rail                                             */
/* -------------------------------------------------------------------------- */

function Toolbar({
  search, onSearch, pinned, mode, onMode, onRefresh, refreshing, railOpen, onRail,
  filterCount, hiddenCount, onColumns,
}) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 p-2 px-3 border-bottom">
      <div className="searchbox" style={{ maxWidth: 230 }}>
        <i className="bi bi-search" aria-hidden="true" />
        <input
          type="search"
          placeholder="Search this list…"
          style={{ paddingRight: 12 }}
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          aria-label="Search this list"
        />
      </div>
      {pinned && (
        <span className="pill pill-brand"><i className="bi bi-pin-angle-fill" aria-hidden="true" />Pinned view</span>
      )}

      <div className="ms-auto d-flex align-items-center gap-2">
        <div className="view-switch">
          {[['table', 'bi-table', 'Table'], ['split', 'bi-layout-sidebar-inset', 'Split view'], ['kanban', 'bi-kanban', 'Kanban']]
            .map(([key, icon, label]) => (
              <button
                key={key}
                type="button"
                className={mode === key ? 'active' : ''}
                title={label}
                aria-pressed={mode === key}
                onClick={() => onMode(key)}
              >
                <i className={`bi ${icon}`} aria-hidden="true" />
              </button>
            ))}
        </div>
        <button type="button" className="icon-btn" title="Refresh" onClick={onRefresh} disabled={refreshing}>
          {/* The button's disabled state is the whole indicator: the top
              progress bar already shows a fetch is in flight, and a second
              spinning thing beside it competes with it for attention. */}
          <i className="bi bi-arrow-clockwise" aria-hidden="true" />
        </button>
        <button type="button" className="icon-btn" title="Edit columns" onClick={onColumns}>
          <i className="bi bi-sliders" aria-hidden="true" />
          {hiddenCount > 0 && <span className="count">{hiddenCount}</span>}
        </button>
        <button
          type="button"
          className="icon-btn"
          title={railOpen ? 'Hide filters' : 'Show filters'}
          aria-expanded={railOpen}
          onClick={onRail}
        >
          <i className="bi bi-funnel" aria-hidden="true" />
          {filterCount > 0 && <span className="count">{filterCount}</span>}
        </button>
      </div>
    </div>
  );
}

/**
 * The mass action bar.
 *
 * Two of the prototype's five buttons work, two explain why they cannot, and
 * Delete works with the reason the database requires. They stay in the
 * prototype's order so the bar is recognisably the same bar.
 */
function BulkBar({
  count, canEdit, canDelete, canAssign, busy, onStatus, onCampaign, onDelete, onChangeOwner, onEmail, onClear,
}) {
  return (
    <div
      className="d-flex flex-wrap align-items-center gap-2 px-3 py-2"
      style={{ background: 'var(--brand-50)', borderBottom: '1px solid var(--brand-200)' }}
    >
      <span className="t-sm fw-650">{count} selected</span>
      <div className="vr mx-1" />
      {canAssign && (
        <button type="button" className="btn btn-soft btn-sm-x" onClick={onChangeOwner}>
          <i className="bi bi-person-check me-1" aria-hidden="true" />Change Owner
        </button>
      )}
      <button type="button" className="btn btn-soft btn-sm-x" disabled={!canEdit || busy} onClick={onStatus}>
        <i className="bi bi-flag me-1" aria-hidden="true" />Change Status
      </button>
      <button type="button" className="btn btn-soft btn-sm-x" onClick={onCampaign}>
        <i className="bi bi-megaphone me-1" aria-hidden="true" />Add to Campaign
      </button>
      <button type="button" className="btn btn-soft btn-sm-x" onClick={onEmail}>
        <i className="bi bi-envelope me-1" aria-hidden="true" />Send Email
      </button>
      <button
        type="button"
        className="btn btn-soft btn-sm-x text-danger"
        disabled={!canDelete || busy}
        onClick={onDelete}
      >
        <i className="bi bi-trash me-1" aria-hidden="true" />Delete
      </button>
      <button type="button" className="btn btn-ghost btn-sm-x ms-auto" onClick={onClear}>Clear selection</button>
    </div>
  );
}

/**
 * The filter rail.
 *
 * The prototype's blocks are decorative divs — "Lead Status not equal to
 * Converted, Junk", "Created Date equals LAST 90 DAYS" — describing the filters
 * the view already applies. That description is worth keeping, so the "Matching
 * all of these filters" block shows the *current* view's real parameters rather
 * than three fixed rows. What sits below it are controls that work.
 */
function FilterRail({
  open, view, statusFilter, onStatusToggle, scoreGte, onScoreGte, quick, onQuick,
  partnerId, onPartnerId, partnerOptions, onClear, onClose, filterCount,
}) {
  const QUICK = [
    ['hot', 'Hot only'],
    ['unassigned', 'Unassigned'],
    ['nofollowup', 'No follow-up'],
    ['overdue', 'Overdue'],
  ];

  return (
    <aside className={`filter-rail ${open ? 'open' : ''}`} aria-label="List filters" aria-hidden={!open}>
      <div className="fr-inner">
        <div className="d-flex align-items-center gap-2 mb-3">
          <div className="fw-650 t-sm">Filters</div>
          {/* A link, not a button: `.btn-ghost` draws a border and a hover
              plate, which put a boxed control where the prototype has plain
              text and made the rail's header look like a toolbar. */}
          <button
            type="button"
            className="ms-auto t-xs fw-650 border-0 bg-transparent p-0"
            style={{ color: filterCount === 0 ? 'var(--ink-4)' : 'var(--brand-600)' }}
            disabled={filterCount === 0}
            onClick={onClear}
          >
            Remove all
          </button>
          <button type="button" className="icon-btn" style={{ width: 26, height: 26, fontSize: 12 }}
                  onClick={onClose} aria-label="Close filters">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <div className="filter-block">
          <div className="fb-label">Show me</div>
          <div className="fb-value">{view.name}</div>
        </div>

        <div className="filter-block">
          <div className="fb-label">Applied by this view</div>
          {Object.entries(view.params).map(([k, v]) => (
            <div key={k} className="mt-2 p-2 px-3" style={{ border: '1px solid var(--line)', borderRadius: 9, background: '#fff' }}>
              <div className="fb-value">{PARAM_LABEL[k] ?? k}</div>
              <div className="fb-op">is <span className="ink-2 fw-600">{v}</span></div>
            </div>
          ))}
        </div>

        <div className="filter-block">
          <div className="fb-label">Lead Status</div>
          <div className="d-flex flex-wrap gap-1 mt-2">
            {STATUS_OPTIONS.map(([code, label]) => (
              <button
                key={code}
                type="button"
                className={`chip ${statusFilter.includes(code) ? 'active' : ''}`}
                aria-pressed={statusFilter.includes(code)}
                onClick={() => onStatusToggle(code)}
              >
                {label}
              </button>
            ))}
          </div>
          {statusFilter.length > 0 && (
            <div className="t-2xs ink-4 mt-1">Overrides the view&rsquo;s own Lead Status filter above.</div>
          )}
        </div>

        <div className="filter-block">
          <div className="fb-label">Lead score</div>
          <div className="d-flex align-items-center gap-2 mt-2">
            <span className="fb-op">at least</span>
            <input
              className="form-control form-control-sm"
              type="number" min="0" max="100" step="5" style={{ width: 84 }}
              value={scoreGte}
              onChange={(e) => onScoreGte(e.target.value)}
              aria-label="Minimum lead score"
            />
          </div>
        </div>

        {partnerOptions && (
          <div className="filter-block">
            <div className="fb-label">Partner</div>
            <div className="mt-2">
              <ChipSelect
                label="Partner" allLabel="All partners" value={partnerId} onChange={onPartnerId}
                options={partnerOptions}
              />
            </div>
          </div>
        )}

        <div className="filter-block">
          <div className="fb-label">Quick filters</div>
          <div className="d-flex flex-wrap gap-1 mt-2">
            {QUICK.map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`chip ${quick[key] ? 'active' : ''}`}
                aria-pressed={Boolean(quick[key])}
                onClick={() => onQuick(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* No Save button. Saving a filter set is `cfg.saved_view` — §10, not
            implemented — and a Save that discards what you typed is worse than
            no Save at all. */}
        <div className="t-2xs ink-4">
          Filters apply as you set them. They cannot be saved yet: §10 saved views
          are not implemented.
        </div>
      </div>
    </aside>
  );
}

const PARAM_LABEL = {
  status: 'Lead Status', owner_id: 'Owner', followup: 'Next Follow-up',
  sla: 'SLA', rating: 'Rating', score_gte: 'Lead Score', referred_by_partner_id: 'Partner',
};

